import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Container } from "../components/Container";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { TextArea } from "../components/TextArea";
import { FormGroup } from "../components/FormGroup";
import { FormLabel } from "../components/FormLabel";
import { FormActions } from "../components/FormActions";
import {
  getAllApiKeysApi,
  createApiKeyApi,
  deactivateApiKeyApi,
  deleteApiKeyApi,
} from "../api/apiKeysApi";
import type { RestApiKey, CreateApiKey } from "@KassaroAB/shared/types/apiKey";
import { ApiKeyPermission } from "@KassaroAB/shared/types/enums/ApiKeyPermission";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { formatDate } from "../utils/dateUtils";

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing.x4}px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: ${fontSizes.xlarge}px;
  font-weight: 600;
  color: ${colors.text};
`;

const FormContainer = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x3}px;
  margin-bottom: ${spacing.x4}px;
`;

const Input = styled.input`
  padding: ${spacing.x2}px;
  border: 1px solid ${colors.border};
  border-radius: 4px;
  font-size: ${fontSizes.regular}px;
  color: ${colors.text};
  background-color: ${colors.backgroundLight};
`;


const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
  font-size: ${fontSizes.regular}px;
  color: ${colors.text};
  cursor: pointer;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;


const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const KeyCard = styled(Card)`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: ${spacing.x3}px;
`;

const KeyInfo = styled.div`
  flex: 1;
`;

const KeyTitle = styled.div`
  font-size: ${fontSizes.large}px;
  font-weight: 600;
  color: ${colors.text};
  margin-bottom: ${spacing.x1}px;
`;

const KeyMeta = styled.div`
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
  margin-bottom: ${spacing.x1}px;
`;

const StatusBadge = styled.span<{ $isActive: boolean }>`
  background-color: ${({ $isActive }) =>
    $isActive ? colors.successBackground : colors.warningBackground};
  color: ${({ $isActive }) => ($isActive ? colors.success : colors.warning)};
  padding: ${spacing.x05}px ${spacing.x2}px;
  border-radius: 4px;
  font-size: ${fontSizes.small}px;
  font-weight: 500;
  margin-left: ${spacing.x2}px;
`;

const KeyActions = styled.div`
  display: flex;
  gap: ${spacing.x2}px;
`;

const PlainKeyDisplay = styled.div`
  padding: ${spacing.x2}px;
  background-color: ${colors.backgroundDark};
  border-radius: 4px;
  font-family: monospace;
  font-size: ${fontSizes.small}px;
  word-break: break-all;
  margin-top: ${spacing.x2}px;
`;

export const ApiKeysView = () => {
  const { t } = useTranslation();
  const [isCreating, setIsCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState<ApiKeyPermission[]>([
    ApiKeyPermission.BUNDLES_UPLOAD,
  ]);
  const queryClient = useQueryClient();

  const { data: apiKeys, isLoading, error } = useQuery({
    queryKey: ["apiKeys"],
    queryFn: getAllApiKeysApi,
  });

  const { mutate: createMutation, isPending: isCreatePending } = useMutation({
    mutationFn: createApiKeyApi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["apiKeys"] });
      setCreatedKey(data.plainKey);
      setIsCreating(false);
      setName("");
      setDescription("");
      setSelectedPermissions([ApiKeyPermission.BUNDLES_UPLOAD]);
    },
  });

  const { mutate: deactivateMutation, isPending: isDeactivatePending } = useMutation({
    mutationFn: deactivateApiKeyApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["apiKeys"] });
    },
  });

  const { mutate: deleteMutation, isPending: isDeletePending } = useMutation({
    mutationFn: deleteApiKeyApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["apiKeys"] });
    },
  });

  const handlePermissionToggle = (permission: ApiKeyPermission) => {
    setSelectedPermissions((prev) =>
      prev.includes(permission)
        ? prev.filter((p) => p !== permission)
        : [...prev, permission],
    );
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    const data: CreateApiKey = {
      name,
      description: description || null,
      permissions: selectedPermissions,
    };
    createMutation(data);
  };

  const handleDeactivate = (id: string) => {
    if (window.confirm(t("apiKeysView.deactivateConfirm"))) {
      deactivateMutation(id);
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm(t("apiKeysView.deleteConfirm"))) {
      deleteMutation(id);
    }
  };

  return (
    <Container>
      <PageHeader>
        <Title>{t("apiKeysView.title")}</Title>
      </PageHeader>

      {createdKey && (
        <Card style={{ padding: spacing.x3, backgroundColor: colors.successBackground, marginBottom: spacing.x3 }}>
          <div style={{ fontWeight: 600, marginBottom: spacing.x2 }}>{t("apiKeysView.createdTitle")}</div>
          <div style={{ marginBottom: spacing.x1 }}>{t("apiKeysView.saveKeyHint")}</div>
          <PlainKeyDisplay>{createdKey}</PlainKeyDisplay>
          <Button
            onClick={() => {
              navigator.clipboard.writeText(createdKey).then(() => {
                window.alert(t("common.copiedToClipboard"));
              });
            }}
            style={{ marginTop: spacing.x2 }}
          >
            {t("apiKeysView.copyClipboard")}
          </Button>
          <Button
            onClick={() => setCreatedKey(null)}
            style={{ marginTop: spacing.x2, marginLeft: spacing.x2, backgroundColor: colors.backgroundDark }}
          >
            {t("common.close")}
          </Button>
        </Card>
      )}

      {!isCreating && !createdKey && (
        <div style={{ marginBottom: spacing.x3 }}>
          <Button onClick={() => setIsCreating(true)}>{t("apiKeysView.createButton")}</Button>
        </div>
      )}

      {isCreating && (
        <FormContainer>
          <form onSubmit={handleCreate}>
            <FormGroup>
              <FormLabel>{t("apiKeysView.name")}</FormLabel>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder={t("apiKeysView.namePlaceholder")}
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>{t("apiKeysView.descriptionOptional")}</FormLabel>
              <TextArea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("apiKeysView.descriptionPlaceholder")}
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>{t("apiKeysView.permissions")}</FormLabel>
              <CheckboxGroup>
                {Object.values(ApiKeyPermission).map((permission) => (
                  <CheckboxLabel key={permission}>
                    <Checkbox
                      type="checkbox"
                      checked={selectedPermissions.includes(permission)}
                      onChange={() => handlePermissionToggle(permission)}
                    />
                    {permission}
                  </CheckboxLabel>
                ))}
              </CheckboxGroup>
            </FormGroup>

            <FormActions>
              <Button
                type="button"
                onClick={() => setIsCreating(false)}
                style={{ backgroundColor: colors.backgroundDark }}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={isCreatePending}>
                {t("apiKeysView.create")}
              </Button>
            </FormActions>
          </form>
        </FormContainer>
      )}

      {error && (
        <Card style={{ padding: spacing.x3, backgroundColor: colors.dangerBackground, color: colors.danger }}>
          {t("apiKeysView.errorLoad")}
        </Card>
      )}

      {isLoading ? (
        <Card style={{ padding: spacing.x4, textAlign: "center", color: colors.textMuted }}>
          {t("apiKeysView.loading")}
        </Card>
      ) : (
        <ListContainer>
          {apiKeys && apiKeys.length === 0 ? (
            <Card style={{ padding: spacing.x4, textAlign: "center", color: colors.textMuted }}>
              {t("apiKeysView.empty")}
            </Card>
          ) : (
            apiKeys?.map((key: RestApiKey) => (
              <KeyCard key={key.id}>
                <KeyInfo>
                  <KeyTitle>
                    {key.name}
                    <StatusBadge $isActive={key.isActive !== false}>
                      {key.isActive !== false ? t("apiKeysView.active") : t("apiKeysView.inactive")}
                    </StatusBadge>
                  </KeyTitle>
                  {key.description && <KeyMeta>{key.description}</KeyMeta>}
                  <KeyMeta>{t("apiKeysView.permissionsLabel", { list: key.permissions.join(", ") })}</KeyMeta>
                  <KeyMeta>{t("apiKeysView.createdLabel", { date: formatDate(key.createdAt) })}</KeyMeta>
                  {key.lastUsedAt && (
                    <KeyMeta>{t("apiKeysView.lastUsedLabel", { date: formatDate(key.lastUsedAt) })}</KeyMeta>
                  )}
                </KeyInfo>
                <KeyActions>
                  {key.isActive !== false && (
                    <Button
                      onClick={() => handleDeactivate(key.id)}
                      disabled={isDeactivatePending}
                      style={{ backgroundColor: colors.warning }}
                    >
                      {t("apiKeysView.deactivate")}
                    </Button>
                  )}
                  <Button
                    onClick={() => handleDelete(key.id)}
                    disabled={isDeletePending}
                    style={{ backgroundColor: colors.danger }}
                  >
                    {t("apiKeysView.delete")}
                  </Button>
                </KeyActions>
              </KeyCard>
            ))
          )}
        </ListContainer>
      )}
    </Container>
  );
};

